import { Task } from 'redux-saga'
import { SagaReturnType, call, cancel, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects'

import { AuthUser } from '@/core/classes/Auth'
import consultationModel, { Consultation } from '@/core/models/consultation.model'
import documentModel from '@/core/models/document.model'
import patientModel, { Patient } from '@/core/models/patient.model'
import { SearchResponse } from '@/core/types/response'

import {
  startBackgroundCheckDownloadSpeed as startBackgroundCheckDownloadSpeedAction,
  stopBackgroundCheckDownloadSpeed as stopBackgroundCheckDownloadSpeedAction,
  startBackgroundCheckUploadSpeed as startBackgroundCheckUploadSpeedAction,
  stopBackgroundCheckUploadSpeed as stopBackgroundCheckUploadSpeedAction,
} from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  getTotalPatient as getTotalPatientAction,
  setTotalPatient as setTotalPatientAction,
  setDownloadSpeed as setDownloadSpeedAction,
  setUploadSpeed as setUploadSpeedAction,
  getPatientsByWeekByEtablissement as getPatientsByWeekByEtablissementAction,
  setPatientsByWeekByEtablissement as setPatientsByWeekByEtablissementAction,
  getConsultationsByWeekByEtablissement as getConsultationsByWeekByEtablissementAction,
  setConsultationByWeekByEtablissement as setConsultationsByWeekByEtablissementAction,
  getConsultationsStatusCountByEtablissement as getConsultationCountByStatusAction,
  setConsultationsStatusCountByEtablissement as setConsultationCountByStatusAction,
  getConsultationsTypeCountByEtablissement as getConsultationCountByTypeAction,
  setConsultationsTypeCountByEtablissement as setConsultationCountByTypeAction,
  getRankConsultationsCountByEtablissement as getConsultationCountByRankAction,
  setRankConsultationsCountByEtablissement as setConsultationCountByRankAction,
  getMeetingLinkStatistics as getMeetingLinkStatisticsAction,
  setMeetingLinkStatistics as setMeetingLinkStatisticsAction,
  getDownloadSpeed as getDownloadSpeedAction,
  getUploadSpeed as getUploadSpeedAction,
  setDailyStatus,
  setWeeklyConsultations,
  setDailyPatients,
  setWeeklyConsultationsByType,
  setDailyConsultationsReasons,
  setWeeklySpecialtyConsultations,
  getDailyStatus as getDailyStatusAction,
  getWeeklyConsultations as getWeeklyConsultationsAction,
  getDailyPatients as getDailyPatientsAction,
  getWeeklyConsultationsByType as getWeeklyConsultationsByTypeAction,
  getDailyConsultationsReasons as getDailyConsultationsReasonsAction,
  getWeeklySpecialtyConsultations as getWeeklySpecialtyConsultationsAction,
} from '@/redux/reducers/statistic.reducer'

import { blobToBase64 } from '@/utils/helpers'
import adminModel, {
  CountByString,
  CountByWeek,
  DailyConsultationsReasons,
  DailyPatients,
  DailyStatus,
  RankCount,
  WeeklyConsultations,
  WeeklyConsultationsByType,
  WeeklySpecialtyConsultations,
} from '@/core/models/admin.model'
import { PayloadAction } from '@reduxjs/toolkit'
import meetingLinkModel from '@/core/models/meeting-link.model'
import { isStaff } from '@/utils/type-guards'

async function calculateNetworkSpeed(url: string, fileSizeInBytes: number): Promise<number> {
  const start = new Date()
  await fetch(url)
  const end = new Date()

  // Calculate time difference in seconds
  const timeInSeconds = (end.getTime() - start.getTime()) / 1000

  // Convert fileSize to megabits
  const fileSizeInMegabits = (fileSizeInBytes * 8) / (1024 * 1024)

  // Calculate speed in megabits per second
  const speedInMbps = fileSizeInMegabits / timeInSeconds

  return parseInt(speedInMbps.toPrecision(4))
}

function* getTotalPatient() {
  try {
    const { totalCount }: SearchResponse<Patient> = yield call(patientModel.getPatients, { limit: 0 })
    yield put(setTotalPatientAction(totalCount))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getConsultationsByWeek(action: PayloadAction<ReturnType<typeof getConsultationsByWeekByEtablissementAction>['payload']>) {
  try {
    const result: CountByWeek[] = yield call(adminModel.getConsultationsByWeek, action.payload.establishmentId)
    yield put(setConsultationsByWeekByEtablissementAction(result))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getPatientsByWeek(action: PayloadAction<ReturnType<typeof getPatientsByWeekByEtablissementAction>['payload']>) {
  try {
    const result: CountByWeek[] = yield call(adminModel.getPatientsByWeek, action.payload.establishmentId)
    yield put(setPatientsByWeekByEtablissementAction(result))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getConsultationCountByStatus(action: PayloadAction<ReturnType<typeof getConsultationCountByStatusAction>['payload']>) {
  try {
    const result: CountByString[] = yield call(adminModel.getConsultationsByStatus, action.payload.establishmentId)
    yield put(setConsultationCountByStatusAction(result))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getConsultationCountByType(action: PayloadAction<ReturnType<typeof getConsultationCountByTypeAction>['payload']>) {
  try {
    const result: CountByString[] = yield call(adminModel.getConsultationsByType, action.payload.establishmentId)
    yield put(setConsultationCountByTypeAction(result))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getRankConsultationsCountByEtablissement(action: PayloadAction<ReturnType<typeof getConsultationCountByRankAction>['payload']>) {
  try {
    const result: RankCount[] = yield call(adminModel.getConsultationsByStaff, action.payload.establishmentId)
    yield put(setConsultationCountByRankAction(result))
  } catch (exception) {
    console.warn(exception)
  }
}

function* getMeetingLinkStatistics() {
  try {
    const statistics: SagaReturnType<typeof meetingLinkModel.getStatistics> = yield call(meetingLinkModel.getStatistics)
    yield put(setMeetingLinkStatisticsAction(statistics))
  } catch (exception) {}
}

// Saga to fetch daily status statistics
function* getDailyStatus(action: PayloadAction<ReturnType<typeof getDailyStatusAction>['payload']>) {
  try {
    const response: DailyStatus['data'] = yield call(adminModel.getDailyStatus, action.payload.establishmentId, action.payload.date)
    yield put(setDailyStatus(response))
  } catch (error) {
    console.error('Failed to fetch daily status', error)
  }
}

// Saga to fetch weekly consultations
function* getWeeklyConsultations(action: PayloadAction<ReturnType<typeof getWeeklyConsultationsAction>['payload']>) {
  try {
    const response: WeeklyConsultations['data'] = yield call(
      adminModel.getWeeklyConsultations,
      action.payload.establishmentId,
      action.payload.startDate,
      action.payload.endDate
    )
    yield put(setWeeklyConsultations(response))
  } catch (error) {
    console.error('Failed to fetch weekly consultations', error)
  }
}

// Saga to fetch daily new patients
function* getDailyPatients(action: PayloadAction<ReturnType<typeof getDailyPatientsAction>['payload']>) {
  try {
    const response: DailyPatients['data'] = yield call(adminModel.getDailyPatients, action.payload.establishmentId, action.payload.date)
    yield put(setDailyPatients(response))
  } catch (error) {
    console.error('Failed to fetch daily new patients', error)
  }
}

// Saga to fetch weekly consultations by type
function* getWeeklyConsultationsByType(action: PayloadAction<ReturnType<typeof getWeeklyConsultationsByTypeAction>['payload']>) {
  try {
    const response: WeeklyConsultationsByType['data'] = yield call(
      adminModel.getWeeklyConsultationsByType,
      action.payload.establishmentId,
      action.payload.startDate,
      action.payload.endDate
    )
    yield put(setWeeklyConsultationsByType(response))
  } catch (error) {
    console.error('Failed to fetch weekly consultations by type', error)
  }
}

// Saga to fetch daily consultations by main reason
function* getDailyConsultationsReasons(action: PayloadAction<ReturnType<typeof getDailyConsultationsReasonsAction>['payload']>) {
  try {
    const response: DailyConsultationsReasons['data'] = yield call(adminModel.getDailyConsultationsReasons, action.payload.establishmentId, action.payload.date)
    console.log('response', response)
    yield put(setDailyConsultationsReasons(response))
  } catch (error) {
    console.error('Failed to fetch daily consultations by main reason', error)
  }
}

// Saga to fetch weekly specialty consultations
function* getWeeklySpecialtyConsultations(action: PayloadAction<ReturnType<typeof getWeeklySpecialtyConsultationsAction>['payload']>) {
  try {
    const response: WeeklySpecialtyConsultations['data'] = yield call(
      adminModel.getWeeklySpecialtyConsultations,
      action.payload.establishmentId,
      action.payload.startDate,
      action.payload.endDate
    )
    yield put(setWeeklySpecialtyConsultations(response))
  } catch (error) {
    console.error('Failed to fetch weekly specialty consultations', error)
  }
}

/**
 * @param timeInterval in seconds
 */
function* bgGetDownloadSpeed(timeInterval: number) {
  yield call(getDownloadSpeed)
  yield delay(timeInterval * 1000)
}

function* getDownloadSpeed() {
  try {
    const fileSizeInBytes = 4952221
    const speedMbps: number = yield call(
      calculateNetworkSpeed,
      `https://raw.githubusercontent.com/jrquick17/ng-speed-test/02c59e4afde67c35a5ba74014b91d44b33c0b3fe/demo/src/assets/5mb.jpg?nnn=${Math.random()}`,
      fileSizeInBytes
    )
    yield put(setDownloadSpeedAction(speedMbps))
  } catch (error) {
    console.log(error)
  }
}

function* watchBackgroundCheckDownloadSpeed() {
  while (true) {
    // wait for the user start background action
    yield take(startBackgroundCheckDownloadSpeedAction.type)

    // starts the task in the background
    const bgGetDownloadSpeedTask: Task = yield fork(bgGetDownloadSpeed, 60)

    // wait for the user stop action
    yield take(stopBackgroundCheckDownloadSpeedAction.type)

    // cancel the background task
    yield cancel(bgGetDownloadSpeedTask)
  }
}

/**
 * @param timeInterval in seconds
 */
function* bgGetUploadSpeed(timeInterval: number) {
  while (true) {
    yield call(getUploadSpeed)
    yield delay(timeInterval * 1000)
  }
}

function* getUploadSpeed() {
  try {
    const response: Response = yield fetch(
      `https://raw.githubusercontent.com/jrquick17/ng-speed-test/02c59e4afde67c35a5ba74014b91d44b33c0b3fe/demo/src/assets/500kb.jpg?nnn=${Math.random()}`
    )
    const imgBlob: Blob = yield response.blob()
    const imgBase64String: string = yield blobToBase64(imgBlob)
    const startTime = performance.now()
    yield call(documentModel.runUploadSpeedTest, imgBase64String)
    const endTime = performance.now()
    const fileSizeInMegaBytes = imgBlob.size / 1000000
    const duration = (endTime - startTime) / 1000
    const speedMbps = parseFloat(((fileSizeInMegaBytes * 8) / duration).toFixed(2))
    yield put(setUploadSpeedAction(speedMbps))
  } catch (error) {
    console.log(error)
  }
}

function* watchBackgroundCheckUploadSpeed() {
  while (true) {
    // wait for the user start background action
    yield take(startBackgroundCheckUploadSpeedAction.type)

    // starts the task in the background
    const bgGetUploadSpeedTask: Task = yield fork(bgGetUploadSpeed, 60)

    // wait for the user stop action
    yield take(stopBackgroundCheckUploadSpeedAction.type)

    // cancel the background task
    yield cancel(bgGetUploadSpeedTask)
  }
}

const sagas = [
  call(watchBackgroundCheckDownloadSpeed),
  call(watchBackgroundCheckUploadSpeed),
  takeLatest(getDownloadSpeedAction.type, getDownloadSpeed),
  takeLatest(getUploadSpeedAction.type, getUploadSpeed),
  takeLatest(getTotalPatientAction.type, getTotalPatient),
  takeLatest(getConsultationsByWeekByEtablissementAction.type, getConsultationsByWeek),
  takeLatest(getPatientsByWeekByEtablissementAction.type, getPatientsByWeek),
  takeLatest(getConsultationCountByStatusAction.type, getConsultationCountByStatus),
  takeLatest(getConsultationCountByTypeAction.type, getConsultationCountByType),
  takeLatest(getConsultationCountByRankAction.type, getRankConsultationsCountByEtablissement),
  takeLatest(getMeetingLinkStatisticsAction.type, getMeetingLinkStatistics),
  takeLatest(getDailyStatusAction.type, getDailyStatus),
  takeLatest(getWeeklyConsultationsAction.type, getWeeklyConsultations),
  takeLatest(getDailyPatientsAction.type, getDailyPatients),
  takeLatest(getWeeklyConsultationsByTypeAction.type, getWeeklyConsultationsByType),
  takeLatest(getDailyConsultationsReasonsAction.type, getDailyConsultationsReasons),
  takeLatest(getWeeklySpecialtyConsultationsAction.type, getWeeklySpecialtyConsultations),
]

export default sagas
